import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    Flex,
    Text,
    VStack,
    Tag,
    TagLabel,
    TagCloseButton,
    useDisclosure,
    Collapse,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';

const CustomMultiSelect = ({
    options = [],
    value = [],
    onChange,
    placeholder = 'Select options...',
    disabled = false,
    name,
    fontSize = 'sm',
    ...props
}) => {
    const [selectedOptions, setSelectedOptions] = useState(value);
    const { isOpen, onToggle, onClose } = useDisclosure();
    const containerRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [onClose]);

    // Update local state when value prop changes
    useEffect(() => {
        setSelectedOptions(value);
    }, [value]);

    const handleOptionToggle = (option) => {
        const isSelected = selectedOptions.some(
            (selected) => (selected.value || selected) === (option.value || option),
        );
        let newSelection;

        if (isSelected) {
            newSelection = selectedOptions.filter(
                (selected) => (selected.value || selected) !== (option.value || option),
            );
        } else {
            newSelection = [...selectedOptions, option];
        }

        setSelectedOptions(newSelection);
        onChange && onChange(newSelection);
    };

    const handleRemoveOption = (optionToRemove) => {
        const newSelection = selectedOptions.filter(
            (selected) => (selected.value || selected) !== (optionToRemove.value || optionToRemove),
        );
        setSelectedOptions(newSelection);
        onChange && onChange(newSelection);
    };

    const isOptionSelected = (option) => {
        return selectedOptions.some(
            (selected) => (selected.value || selected) === (option.value || option),
        );
    };

    return (
        <Box position="relative" ref={containerRef} {...props}>
            {/* Main Button */}
            <Button
                onClick={onToggle}
                disabled={disabled}
                width="100%"
                height="40px"
                justifyContent="space-between"
                fontWeight="normal"
                fontSize={fontSize}
                bg="white"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
                color={selectedOptions.length > 0 ? 'gray.900' : 'gray.500'}
                _hover={{
                    borderColor: 'gray.300',
                }}
                _focus={{
                    borderColor: 'blue.500',
                    boxShadow: '0 0 0 1px #3182ce',
                }}
                _disabled={{
                    opacity: 0.6,
                    cursor: 'not-allowed',
                }}
                rightIcon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}>
                <Flex align="center" flex={1} overflow="hidden">
                    {selectedOptions.length === 0 ? (
                        <Text color="gray.500" fontSize={fontSize}>
                            {placeholder}
                        </Text>
                    ) : selectedOptions.length === 1 ? (
                        <Text fontSize={fontSize} isTruncated>
                            {selectedOptions[0].label || selectedOptions[0]}
                        </Text>
                    ) : (
                        <Text fontSize={fontSize}>{selectedOptions.length} items selected</Text>
                    )}
                </Flex>
            </Button>

            {/* Selected Options Tags (when multiple selected) */}
            {selectedOptions.length > 1 && (
                <Flex wrap="wrap" gap={1} mt={2}>
                    {selectedOptions.map((option, index) => (
                        <Tag
                            key={`${option.value || option}-${index}`}
                            size="sm"
                            colorScheme="blue"
                            variant="solid">
                            <TagLabel fontSize="xs">{option.label || option}</TagLabel>
                            <TagCloseButton
                                onClick={() => handleRemoveOption(option)}
                                disabled={disabled}
                            />
                        </Tag>
                    ))}
                </Flex>
            )}

            {/* Dropdown Options */}
            <Collapse in={isOpen}>
                <Box
                    position="absolute"
                    top="100%"
                    left={0}
                    right={0}
                    zIndex={1000}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="md"
                    mt={1}
                    maxHeight="200px"
                    overflowY="auto"
                    shadow="md">
                    <VStack spacing={0} align="stretch">
                        {options.length === 0 ? (
                            <Box p={3}>
                                <Text fontSize="sm" color="gray.500" textAlign="center">
                                    No options available
                                </Text>
                            </Box>
                        ) : (
                            options.map((option, index) => (
                                <Box
                                    key={`${option.value || option}-${index}`}
                                    as="button"
                                    type="button"
                                    p={3}
                                    textAlign="left"
                                    fontSize={fontSize}
                                    bg={isOptionSelected(option) ? 'blue.50' : 'white'}
                                    color={isOptionSelected(option) ? 'blue.700' : 'gray.900'}
                                    _hover={{
                                        bg: isOptionSelected(option) ? 'blue.100' : 'gray.50',
                                    }}
                                    _focus={{
                                        bg: isOptionSelected(option) ? 'blue.100' : 'gray.100',
                                        outline: 'none',
                                    }}
                                    borderBottom={index < options.length - 1 ? '1px solid' : 'none'}
                                    borderColor="gray.100"
                                    onClick={() => handleOptionToggle(option)}
                                    disabled={disabled}>
                                    <Flex justify="space-between" align="center">
                                        <Text>{option.label || option}</Text>
                                        {isOptionSelected(option) && (
                                            <Text color="blue.500" fontSize="lg">
                                                ✓
                                            </Text>
                                        )}
                                    </Flex>
                                </Box>
                            ))
                        )}
                    </VStack>
                </Box>
            </Collapse>
        </Box>
    );
};

export default CustomMultiSelect;
